import type { PoolPricingModel, PriceSummaryState, PricingResolutionCardState, PricingSimulatorState } from '.';
import { isSamePoolPricingConfig } from './pricing/configEquality';
import { DEFAULT_POOL_PRICING_CONFIG } from './pricing/defaultConfig';
import { derivePricingResolution } from './pricing/derivePricingResolution';
import { previewDraftSimulation } from './pricing/previewDraftSimulation';
import { effect } from '@preact/signals-core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { SymbolPricesResponse } from '@thinkalpha/locates-ws-client';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import type {
    BorrowStatusKey,
    PoolPricingConfig,
    PoolPricingMethod,
    PoolPricingSimulationResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import debounce from 'lodash.debounce';
import type { InventoryRow } from 'src/admin/models/PoolInventoryModel';
import { isDraftRowId } from 'src/admin/models/PoolInventoryModel/draftRows';
import type { Pool } from 'src/admin/models/PoolListModel';
import {
    getPoolItemPricingQuery,
    getPoolItemsQuery,
    getPoolPricingSimulationQuery,
    getPricingSimulationQuery,
    getSymbolPricesQuery,
} from 'src/queries/labPools';
import { updatePool } from 'src/queries/labPoolsMutations';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Debounce before re-simulating after a symbol keystroke or config
 * tweak, so live editing fires one request per pause instead of one per
 * change.
 */
const SIMULATOR_DEBOUNCE_MS = 300;

/**
 * Role that grants the price-index endpoints to non-superusers (the
 * price summary's backing data).
 */
const PRICE_INDEX_ACCESS_ROLE = 'locates::allow-price-index-access';

/** Everything the pricing model needs from its owner (the pool side). */
export interface PoolPricingModelDeps {
    /** The pool whose pricing this is; part of the instance's identity. */
    readonly poolId: string;
    readonly queryClient: ReactBindings['QueryClient'];
    readonly store: ReactBindings['Store'];
    /**
     * This pool's live payload from the list cache. The persisted-config
     * baseline seeds from it at construction and reseats when the server
     * copy changes under a clean draft.
     */
    readonly pool: ReadonlySignal<Pool | null>;
    /** Reader access on the pool — gates the sidecard and simulator. */
    readonly canRead: ReadonlySignal<boolean>;
    /** Owner access on the pool — gates config edits (the pool PATCH). */
    readonly canOwn: ReadonlySignal<boolean>;
    /** Live superuser session (mode enabled), for the simulator's arbitrary-config path. */
    readonly hasSuperuserSession: ReadonlySignal<boolean>;
    /** Active-user roles (masq-aware) for the price-index gate. */
    readonly userRoles: ReadonlySignal<string[]>;
    /**
     * Displayed inventory rows (server rows with staged edits applied) —
     * the sidecard resolves the hovered row through these.
     */
    readonly inventoryRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /** Pending draft rows, so the price summary can open from them too. */
    readonly pendingDraftRows: ReadonlySignal<ReadonlyArray<InventoryRow>>;
    /**
     * A config PATCH succeeded and the pool list was refetched — the
     * owner refreshes its caches and bumps its freshness stamp.
     */
    onConfigSaved(): Promise<void> | void;
}

/** See {@link PoolPricingModel}. */
export class PoolPricingModelImpl implements PoolPricingModel {
    readonly #disposableStack = new DisposableStack();
    readonly #deps: PoolPricingModelDeps;

    readonly poolId: string;
    readonly pool: ReadonlySignal<Pool | null>;

    readonly canViewPricingResolution: ReadonlySignal<boolean>;
    readonly canEditPricingConfig: ReadonlySignal<boolean>;
    readonly canViewPriceSummary: ReadonlySignal<boolean>;

    // The baseline seeds from the pool payload's persisted `pricing` (or
    // the default config while the pool has none); submit PATCHes the pool.
    readonly #saved = signal<PoolPricingConfig>(DEFAULT_POOL_PRICING_CONFIG);
    readonly #draft = signal<PoolPricingConfig>(DEFAULT_POOL_PRICING_CONFIG);
    /** Whether the pool has a pricing config persisted server-side. */
    readonly #hasServerPricing = signal(false);
    // Draft counterpart of `#hasServerPricing`: the Pricing Priority
    // toggle (PLAT-13011). While off, the default config seeded into the
    // draft is only editor scaffolding, not a savable state.
    readonly #enabledDraft = signal(false);
    readonly #confirmOpen = signal(false);
    readonly #isSaving = signal(false);

    readonly #resolutionCard = signal<PricingResolutionCardState | null>(null);
    /**
     * Monotonic show-token: settlement handlers capture it and drop their
     * result when a newer show has started since, so a stale response (the
     * row's price was edited between hovers, changing the request) can
     * never overwrite the newer card.
     */
    #resolutionShow = 0;

    readonly #priceSummary = signal<PriceSummaryState | null>(null);
    /**
     * Monotonic open-token: settlement handlers capture it and drop their
     * result when a newer open (or a close) has happened since. The symbol
     * guard alone cannot tell two opens of the SAME symbol apart — e.g. a
     * lagging providers lookup from another firm's pool must not stamp
     * that firm's names onto a reopened dialog.
     */
    #priceSummaryOpen = 0;

    readonly #simulatorSymbol = signal('');
    readonly #simulatorState = signal<PricingSimulatorState | null>(null);
    /**
     * Monotonic run token. Runs snapshot it on start and drop their result
     * when a newer run has started since — the debounced trigger plus the
     * query-cache dedupe make overlaps rare, but symbol edits mid-flight
     * must never let a stale response overwrite a newer one.
     */
    #simulatorRun = 0;

    constructor(deps: PoolPricingModelDeps) {
        this.#deps = deps;
        this.poolId = deps.poolId;
        this.pool = deps.pool;

        this.canViewPricingResolution = computed(() => deps.canRead.value);
        this.canEditPricingConfig = computed(() => deps.canOwn.value);
        this.canViewPriceSummary = computed(
            () => deps.hasSuperuserSession.value || deps.userRoles.value.includes(PRICE_INDEX_ACCESS_ROLE),
        );

        // Seed from whatever the list cache already has; the reseat effect
        // below covers the payload arriving (or changing) later.
        const initialPricing = deps.pool.peek()?.pricing ?? null;
        const initialSaved = initialPricing ?? DEFAULT_POOL_PRICING_CONFIG;
        this.#saved.value = initialSaved;
        this.#draft.value = initialSaved;
        this.#hasServerPricing.value = initialPricing !== null;
        this.#enabledDraft.value = initialPricing !== null;

        // Reseat when the server copy changes under a clean draft
        // (PLAT-12937). Only a non-null pricing copy reseats — a null one
        // is just the stale cache between a successful PATCH and its
        // refetch, and must not clobber the freshly saved config.
        this.#disposableStack.defer(
            effect(() => {
                const serverPricing = deps.pool.value?.pricing ?? null;
                if (serverPricing === null) return;
                this.#hasServerPricing.value = true;
                // The toggle follows the arriving server copy: it can only
                // have been off (clean) or on (pending enable) here, and a
                // persisted config means "on" either way.
                this.#enabledDraft.value = true;
                if (isSamePoolPricingConfig(serverPricing, this.#saved.peek())) return;
                const wasClean = isSamePoolPricingConfig(this.#draft.peek(), this.#saved.peek());
                this.#saved.value = serverPricing;
                if (wasClean) this.#draft.value = serverPricing;
            }),
        );

        // When `canViewPriceSummary` flips off (superuser session expires
        // or the price-index role is revoked) close the summary: it would
        // otherwise keep exposing the previous prices, and an in-flight
        // request could still populate it after revocation.
        this.#disposableStack.defer(
            effect(() => {
                if (!this.canViewPriceSummary.value) {
                    this.closePriceSummary();
                }
            }),
        );

        // Re-simulate (debounced) whenever the simulator symbol or the draft
        // config changes, so hierarchy / minimum-price edits preview live.
        const scheduleSimulation = debounce(() => void this.#runSimulation(), SIMULATOR_DEBOUNCE_MS);
        this.#disposableStack.defer(
            effect(() => {
                // Subscribe to the run inputs; the debounced runner re-reads
                // them fresh when it fires.
                void this.#simulatorSymbol.value;
                void this.#draft.value;
                scheduleSimulation();
            }),
        );
        this.#disposableStack.defer(() => {
            scheduleSimulation.cancel();
            // Orphan any in-flight work: settlements check their tokens and
            // drop their results, so no state write or toast lands after
            // disposal.
            this.#simulatorRun++;
            this.#resolutionShow++;
            this.#priceSummaryOpen++;
        });
    }

    // --- Per-row resolution sidecard ---
    readonly pricingResolutionCard: ReadonlySignal<PricingResolutionCardState | null> = computed(
        () => this.#resolutionCard.value,
    );

    showPricingResolution(rowId: string): void {
        if (!this.canViewPricingResolution.value) return;
        // Draft rows have no server-side item to price yet.
        const row = this.#deps.inventoryRows.value.find((r) => r.id === rowId);
        if (!row || isDraftRowId(row.id)) return;
        const show = ++this.#resolutionShow;

        const query = getPoolItemPricingQuery(this.poolId, rowId);
        // Seed from the cache so a re-hover opens `ready` without a loader
        // flash; `fetchQuery` still revalidates when the entry is stale and
        // the settle path below reconciles the state.
        const cached = this.#deps.queryClient.getQueryData<PoolPricingSimulationResponse>(query.queryKey);
        this.#resolutionCard.value = cached
            ? { rowId, status: 'ready', resolution: derivePricingResolution(cached) }
            : { rowId, status: 'loading' };
        void this.#deps.queryClient.fetchQuery(query).then(
            (response) => {
                if (show !== this.#resolutionShow) return;
                if (this.#resolutionCard.value?.rowId !== rowId) return;
                this.#resolutionCard.value = {
                    rowId,
                    status: 'ready',
                    resolution: derivePricingResolution(response),
                };
            },
            (error: unknown) => {
                if (show !== this.#resolutionShow) return;
                const current = this.#resolutionCard.value;
                if (current?.rowId !== rowId) return;
                // A failed revalidation must not clobber a usable cached
                // view: keep the last-known-good resolution and stay quiet.
                if (current.status === 'ready') return;
                // 400 is the backend's "pool has no pricing configured" —
                // an expected state for unconfigured pools, not a failure.
                if (isAxiosError(error) && error.response?.status === 400) {
                    this.#resolutionCard.value = { rowId, status: 'not-configured' };
                    return;
                }
                this.#resolutionCard.value = { rowId, status: 'error' };
                this.#errorToast('admin::pool-management::toast-pricing-resolution-failed', row.symbol, error);
            },
        );
    }

    hidePricingResolution(rowId: string): void {
        // Guarded per row: when hovering quickly from row A to row B, A's
        // teardown can run after B's show — only A's own state may clear.
        if (this.#resolutionCard.value?.rowId !== rowId) return;
        this.#resolutionCard.value = null;
    }

    /**
     * Error toast for the pricing fetch paths — including the 403s firm
     * admins/owners see while the endpoint-permission rollout is in flight.
     */
    #errorToast(
        key:
            | 'admin::pool-management::toast-pricing-resolution-failed'
            | 'admin::pool-management::toast-simulator-failed'
            | 'admin::pool-management::toast-price-summary-failed',
        symbol: string,
        error: unknown,
    ): void {
        this.#deps.store.dispatch({
            type: 'toast::enqueue',
            toast: {
                message: t(key, { symbol, message: error instanceof Error ? error.message : String(error) }),
                group: 'default',
                variant: 'error',
            },
        });
    }

    // --- Persisted-config draft ---
    readonly savedConfig: ReadonlySignal<PoolPricingConfig> = computed(() => this.#saved.value);
    readonly draftConfig: ReadonlySignal<PoolPricingConfig> = computed(() => this.#draft.value);
    readonly hasSavedConfig: ReadonlySignal<boolean> = computed(() => this.#hasServerPricing.value);
    readonly isEnabled: ReadonlySignal<boolean> = computed(() => this.#enabledDraft.value);
    readonly isDirty: ReadonlySignal<boolean> = computed(
        () =>
            this.#enabledDraft.value !== this.#hasServerPricing.value ||
            (this.#enabledDraft.value && !isSamePoolPricingConfig(this.#draft.value, this.#saved.value)),
    );
    readonly isConfirmOpen: ReadonlySignal<boolean> = computed(() => this.#confirmOpen.value);
    readonly isSaving: ReadonlySignal<boolean> = computed(() => this.#isSaving.value);

    setEnabled(enabled: boolean): void {
        if (!this.canEditPricingConfig.value) return;
        // A persisted config cannot be unset — the pool PATCH only replaces
        // `pricing` — so the toggle locks on once the pool has one, including
        // while the first save's PATCH is still in flight.
        if (!enabled && (this.#hasServerPricing.value || this.#isSaving.value)) return;
        if (!enabled) {
            this.#draft.value = this.#saved.value;
            // The draft is clean again; a lingering submit confirm must not
            // be able to PATCH the reverted config.
            this.#confirmOpen.value = false;
        }
        this.#enabledDraft.value = enabled;
    }

    setHierarchy(hierarchy: PoolPricingMethod[]): void {
        if (!this.canEditPricingConfig.value) return;
        if (!this.#enabledDraft.value) return;
        const current = this.#draft.value.hierarchy;
        // Reject anything that isn't a permutation of the current methods —
        // drag/drop handlers can only reorder, never add or drop a method.
        const isPermutation =
            hierarchy.length === current.length && current.every((method) => hierarchy.includes(method));
        if (!isPermutation) return;
        this.#draft.value = { ...this.#draft.value, hierarchy: [...hierarchy] };
    }

    setMinimumPriceEnabled(enabled: boolean): void {
        if (!this.canEditPricingConfig.value) return;
        if (!this.#enabledDraft.value) return;
        const draft = this.#draft.value;
        // Preserve the persisted-but-disabled floors (backend contract).
        this.#draft.value = {
            ...draft,
            minimumPrice: { enabled, prices: draft.minimumPrice?.prices ?? {} },
        };
    }

    setMinimumPriceForStatus(status: BorrowStatusKey, price: number | undefined): void {
        if (!this.canEditPricingConfig.value) return;
        if (!this.#enabledDraft.value) return;
        const draft = this.#draft.value;
        if (price !== undefined && (!Number.isFinite(price) || price < 0)) return;
        const prices = { ...draft.minimumPrice?.prices };
        if (price === undefined) {
            delete prices[status];
        } else {
            prices[status] = price;
        }
        this.#draft.value = {
            ...draft,
            minimumPrice: { enabled: draft.minimumPrice?.enabled ?? false, prices },
        };
    }

    discardChanges(): void {
        if (!this.canEditPricingConfig.value) return;
        this.#draft.value = this.#saved.value;
        this.#enabledDraft.value = this.#hasServerPricing.value;
        this.#confirmOpen.value = false;
    }

    requestSubmit(): void {
        if (!this.canEditPricingConfig.value) return;
        if (!this.isDirty.value) return;
        this.#confirmOpen.value = true;
    }

    cancelSubmit(): void {
        this.#confirmOpen.value = false;
    }

    confirmSubmit(): void {
        if (!this.canEditPricingConfig.value) return;
        if (this.#isSaving.value) return;
        const poolId = this.poolId;
        const pricing = this.#draft.value;
        this.#isSaving.value = true;
        void updatePool({ poolId, request: { pricing } }).then(
            async (response) => {
                this.#isSaving.value = false;
                this.#confirmOpen.value = false;
                // Reconcile against the server's echo rather than the draft —
                // but never clobber edits the admin made while the PATCH was
                // in flight; those stay dirty against the new baseline.
                const saved = response.pool.pricing ?? pricing;
                const untouched = isSamePoolPricingConfig(this.#draft.value, pricing);
                this.#saved.value = saved;
                if (untouched) this.#draft.value = saved;
                this.#hasServerPricing.value = true;
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-pricing-saved'),
                        group: 'default',
                        variant: 'success',
                    },
                });
                try {
                    await this.#deps.onConfigSaved();
                } catch {
                    // Best-effort cache refresh — a failed invalidation just
                    // leaves the stale pool entry until the next fetch.
                }
            },
            (error: unknown) => {
                this.#isSaving.value = false;
                const message = error instanceof Error ? error.message : String(error);
                this.#deps.store.dispatch({
                    type: 'toast::enqueue',
                    toast: {
                        message: t('admin::pool-management::toast-pricing-save-failed', { message }),
                        group: 'default',
                        variant: 'error',
                    },
                });
            },
        );
    }

    // --- Price summary dialog (PLAT-12938) ---
    readonly priceSummary: ReadonlySignal<PriceSummaryState | null> = computed(() => this.#priceSummary.value);

    openPriceSummary(rowId: string): void {
        if (!this.canViewPriceSummary.value) return;
        const row =
            this.#deps.inventoryRows.value.find((r) => r.id === rowId) ??
            this.#deps.pendingDraftRows.value.find((r) => r.id === rowId);
        if (!row) return;
        const symbol = row.symbol;
        const open = ++this.#priceSummaryOpen;

        // The index identifies providers by id alone; the firm's provider
        // list resolves them to names. Best-effort — a caller who cannot
        // list providers just sees the ids, which is the backend's intent.
        const firmId = this.#deps.pool.value?.firmId;
        const providersQuery = getAllProvidersQuery(firmId);
        const providerNamesFrom = (providers: readonly Provider[] | undefined): Readonly<Record<string, string>> =>
            Object.fromEntries((providers ?? []).map((provider) => [provider.id, provider.name]));

        const query = getSymbolPricesQuery(symbol);
        // Seed from the cache so a re-open shows the levels immediately;
        // `fetchQuery` still revalidates when the entry is stale. The
        // dialog never waits on the (best-effort) providers lookup —
        // prices show as soon as they load and names patch in after.
        const cached = this.#deps.queryClient.getQueryData<SymbolPricesResponse>(query.queryKey);
        let latestNames = providerNamesFrom(this.#deps.queryClient.getQueryData<Provider[]>(providersQuery.queryKey));
        this.#priceSummary.value = cached
            ? { symbol, status: 'ready', prices: cached, providerNames: latestNames }
            : { symbol, status: 'loading' };
        void this.#deps.queryClient
            .fetchQuery(providersQuery)
            .catch(() => undefined)
            .then((providers) => {
                if (open !== this.#priceSummaryOpen) return;
                if (providers === undefined) return;
                latestNames = providerNamesFrom(providers);
                const current = this.#priceSummary.value;
                if (current?.symbol !== symbol || current.status !== 'ready') return;
                this.#priceSummary.value = { ...current, providerNames: latestNames };
            });
        void this.#deps.queryClient.fetchQuery(query).then(
            (prices) => {
                if (open !== this.#priceSummaryOpen) return;
                if (this.#priceSummary.value?.symbol !== symbol) return;
                this.#priceSummary.value = { symbol, status: 'ready', prices, providerNames: latestNames };
            },
            (error: unknown) => {
                if (open !== this.#priceSummaryOpen) return;
                const current = this.#priceSummary.value;
                if (current?.symbol !== symbol) return;
                // A failed revalidation must not clobber usable cached
                // levels: keep the last-known-good view and stay quiet.
                if (current.status === 'ready') return;
                this.#priceSummary.value = { symbol, status: 'error' };
                this.#errorToast('admin::pool-management::toast-price-summary-failed', symbol, error);
            },
        );
    }

    closePriceSummary(): void {
        // Orphan in-flight settlements so a reopened dialog cannot receive
        // a previous open's results.
        this.#priceSummaryOpen++;
        this.#priceSummary.value = null;
    }

    // --- Simulator (PLAT-12937) ---
    readonly simulatorSymbol: ReadonlySignal<string> = computed(() => this.#simulatorSymbol.value);
    readonly simulatorState: ReadonlySignal<PricingSimulatorState | null> = computed(() => this.#simulatorState.value);

    setSimulatorSymbol(value: string): void {
        this.#simulatorSymbol.value = value;
    }

    async #runSimulation(): Promise<void> {
        const run = ++this.#simulatorRun;
        const poolId = this.poolId;
        const symbol = this.#simulatorSymbol.value.trim().toUpperCase();
        if (!symbol || !this.canViewPricingResolution.value) {
            this.#simulatorState.value = null;
            return;
        }
        const config = this.#draft.value;
        const isCleanDraft = isSamePoolPricingConfig(config, this.#saved.value);
        // A pool with no persisted config has no reader-scoped source for
        // the candidate prices — only the superuser-only arbitrary-config
        // simulation can preview it. Everyone else gets a hint instead of
        // a request that can only 403 on every re-run.
        if (!this.#hasServerPricing.value && !this.#deps.hasSuperuserSession.value) {
            this.#simulatorState.value = { symbol, status: 'requires-saved-config' };
            return;
        }
        // Keep a previous result for the same symbol on screen while
        // revalidating (config tweaks), only flash the loader on a new symbol.
        if (this.#simulatorState.value?.symbol !== symbol) {
            this.#simulatorState.value = { symbol, status: 'loading' };
        }
        try {
            // The admin candidate comes from the pool's own inventory record
            // for the symbol, when it has one; the backend resolves the
            // borrow status itself and echoes it on the response.
            const items = await this.#deps.queryClient.fetchQuery(getPoolItemsQuery(poolId, { symbol }));
            // A superseded run stops here instead of firing the simulation
            // request whose result would be discarded anyway.
            if (run !== this.#simulatorRun) return;
            const item = items.items.find((candidate) => candidate.symbol === symbol);
            // A pool with a persisted config previews via its reader-scoped
            // endpoint; only unconfigured pools (superuser sessions, per the
            // gate above) go through the arbitrary-config simulation, which
            // simulates the draft directly.
            const usesPoolEndpoint = this.#hasServerPricing.value;
            const query = usesPoolEndpoint
                ? // An absent stored price is an absent `admin` candidate,
                  // not a zero one — omit rather than default.
                  getPoolPricingSimulationQuery(poolId, { symbol, adminPrice: item?.price })
                : getPricingSimulationQuery({ symbol, adminPrice: item?.price ?? 0, config });
            const serverResponse = await this.#deps.queryClient.fetchQuery(query);
            if (run !== this.#simulatorRun) return;
            // On the pool endpoint, unsaved edits preview by re-resolving
            // the server's market inputs against the draft locally — the
            // arbitrary-config endpoint that could do it server-side is
            // superuser-only.
            const response =
                usesPoolEndpoint && !isCleanDraft ? previewDraftSimulation(config, serverResponse) : serverResponse;
            this.#simulatorState.value = {
                symbol,
                status: 'ready',
                resolution: derivePricingResolution(response),
                borrowStatus: response.borrowStatus,
            };
        } catch (error: unknown) {
            if (run !== this.#simulatorRun) return;
            // Only toast on the transition into the error state — the
            // debounced re-runs (every config tweak or keystroke) would
            // otherwise stack a toast per failed attempt.
            const wasError = this.#simulatorState.value?.status === 'error';
            this.#simulatorState.value = { symbol, status: 'error' };
            if (!wasError) {
                this.#errorToast('admin::pool-management::toast-simulator-failed', symbol, error);
            }
        }
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
